import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { queueAPI } from '../../utils/api';

const DEPARTMENTS = [
    { key: 'OPD', label: 'OPD', color: '#3b82f6', icon: '🏥' },
    { key: 'LAB', label: 'Laboratory', color: '#8b5cf6', icon: '🔬' },
    { key: 'RAD', label: 'Radiology', color: '#0891b2', icon: '🩻' },
    { key: 'PSY', label: 'Psychiatry', color: '#059669', icon: '🧠' },
];

export default function QueueManage() {
    const [selected, setSelected] = useState('OPD');
    const [queue, setQueue] = useState(null);
    const [loading, setLoading] = useState(false);
    const [issuing, setIssuing] = useState(false);
    const [patientName, setPatientName] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await queueAPI.getQueue(selected);
            setQueue(res.data);
        } catch (err) { toast.error(err.response?.data?.message || err.message || 'Failed to load queue'); }
        finally { setLoading(false); }
    }, [selected]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const interval = setInterval(load, 15000);
        return () => clearInterval(interval);
    }, [load]);

    const issueToken = async () => {
        if (!patientName.trim()) return toast.error('Please enter patient name');
        setIssuing(true);
        try {
            const res = await queueAPI.issueToken({ department: selected, patient_name: patientName });
            toast.success(`Token issued: ${res.data.token.token_number}`);
            setPatientName('');
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to issue token');
        } finally { setIssuing(false); }
    };

    const callNext = async () => {
        try {
            const res = await queueAPI.callNext(selected);
            if (res.data.token) {
                toast.success(`Calling: ${res.data.token.token_number} — ${res.data.token.patient_name || ''}`);
            } else {
                toast.info('No patients waiting');
            }
            load();
        } catch { toast.error('Failed to call next'); }
    };

    const resetQueue = async () => {
        if (!window.confirm(`Reset ${selected} queue? All waiting tokens will be marked done.`)) return;
        try {
            await queueAPI.resetQueue(selected);
            toast.success('Queue reset successfully');
            load();
        } catch { toast.error('Failed to reset queue'); }
    };

    const dept = DEPARTMENTS.find(d => d.key === selected);

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e3a5f', margin: 0 }}>🎫 Queue Management</h2>
                <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>Issue tokens and manage patient queue</p>
            </div>

            {/* Department Tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
                {DEPARTMENTS.map(d => (
                    <button key={d.key} onClick={() => setSelected(d.key)}
                        style={{ padding: '10px 20px', background: selected === d.key ? d.color : 'white', color: selected === d.key ? 'white' : '#374151', border: `2px solid ${d.color}`, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        {d.icon} {d.label}
                    </button>
                ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 20 }}>
                {/* Left — Issue Token */}
                <div>
                    {/* Current Serving */}
                    <div style={{ background: 'white', borderRadius: 12, border: `2px solid ${dept.color}`, padding: 24, textAlign: 'center', marginBottom: 16 }}>
                        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, letterSpacing: 1 }}>NOW SERVING</div>
                        <div style={{ fontSize: 56, fontWeight: 900, color: dept.color, letterSpacing: 2, margin: '8px 0' }}>
                            {queue?.current_serving?.token_number || '---'}
                        </div>
                        {queue?.current_serving?.patient_name && (
                            <div style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>{queue.current_serving.patient_name}</div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: 16 }}>
                            <div>
                                <div style={{ fontSize: 24, fontWeight: 800, color: '#f59e0b' }}>{queue?.waiting_count || 0}</div>
                                <div style={{ fontSize: 11, color: '#64748b' }}>Waiting</div>
                            </div>
                            <div>
                                <div style={{ fontSize: 24, fontWeight: 800, color: '#22c55e' }}>{queue?.done_count || 0}</div>
                                <div style={{ fontSize: 11, color: '#64748b' }}>Done</div>
                            </div>
                        </div>
                    </div>

                    {/* Issue Token */}
                    <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20, marginBottom: 12 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', marginBottom: 12 }}>Issue New Token</div>
                        <input
                            value={patientName}
                            onChange={e => setPatientName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && issueToken()}
                            placeholder="Patient name..."
                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box', marginBottom: 10 }}
                        />
                        <button onClick={issueToken} disabled={issuing}
                            style={{ width: '100%', padding: '10px', background: dept.color, color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                            {issuing ? 'Issuing...' : `+ Issue ${selected} Token`}
                        </button>
                    </div>

                    {/* Controls */}
                    <button onClick={callNext}
                        style={{ width: '100%', padding: '12px', background: '#059669', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', marginBottom: 8 }}>
                        ▶ Call Next Patient
                    </button>
                    <button onClick={resetQueue}
                        style={{ width: '100%', padding: '10px', background: 'white', color: '#dc2626', border: '1px solid #dc2626', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        🔄 Reset Queue
                    </button>
                </div>

                {/* Right — Waiting List */}
                <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <div style={{ padding: '14px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>Waiting List</span>
                        <button onClick={load} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>🔄</button>
                    </div>
                    {loading ? (
                        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading...</div>
                    ) : queue?.waiting?.length === 0 ? (
                        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                            <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
                            No patients waiting
                        </div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc' }}>
                                    {['#', 'Token', 'Patient Name', 'Time'].map(h => (
                                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {queue?.waiting?.map((t, i) => (
                                    <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '10px 14px', fontSize: 13, color: '#64748b' }}>{i + 1}</td>
                                        <td style={{ padding: '10px 14px' }}>
                                            <span style={{ background: dept.color, color: 'white', borderRadius: 6, padding: '3px 10px', fontSize: 13, fontWeight: 700 }}>
                                                {t.token_number}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 600 }}>{t.patient_name || '—'}</td>
                                        <td style={{ padding: '10px 14px', fontSize: 11, color: '#64748b' }}>
                                            {new Date(t.created_at).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' })}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}
